import React, { useState, useEffect } from "react";
import useFetch from "../../../../hooks/useFetch";
import { useForm } from "react-hook-form";
import { useAuth } from "../../../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { AuthPanel, AUTH_FIELD } from "../../../shares/landing/LandingWidgets";

/**
 * Login dialog.
 *
 * Auth wiring is untouched: same token-auth POST, same loginDispatch, same
 * admin/student redirect. What changed is the shell and the field treatment -
 * see AuthPanel and AUTH_FIELD. Errors now sit under the field they belong to
 * instead of stacking at the top of the form, and the show/hide password
 * control is a real button rather than a bare <span>.
 */
const Login = ({ close }) => {
  const [username, setUsername] = useState();
  const [password, setPassword] = useState();
  const [showPassword, setShowPassword] = useState(false);

  const [call, setCall] = useState({ username: "", password: "" });
  const [token, setToken] = useState();

  const { loginDispatch, isLogin, admin, setAdmin } = useAuth();

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      username: call.username,
      password: call.password,
    },
  });

  const login = useFetch({
    method: "POST",
    url: `token-auth`,
    noHeader: true,
    trigger: false,
    data: call,
    setter: setToken,
    errMessage: () =>
      toast.error("دوباره تلاش کنید", {
        position: "bottom-left",
        rtl: true,
        autoClose: 2000,
      }),
  });

  const submitForm = () => {
    if (username !== "" && password !== "") {
      const formData = new FormData();
      formData.append("username", username);
      formData.append("password", password);

      setCall(formData);
      login.reFetch();
    }
  };

  useEffect(() => {
    if (token) {
      loginDispatch({ type: "LOGIN", token: token?.token });
      setAdmin(token?.memo_cache);
    }
  }, [token]);

  useEffect(() => {
    if (isLogin === true && admin === false) navigate("/dashboard/home");
    else if (isLogin === true && admin === true) {
      navigate("admin/groups");
    }
  }, [isLogin]);

  return (
    <AuthPanel
      title="ورود به سامانه"
      description="اگر دانش آموز هستید باید کد ملی خود را وارد کنید."
      close={close}
      footer={
        <p className="text-base leading-[2] text-ink-muted">
          درصورتی که مشخصات ورود به سامانه آموزشی عطار را دریافت نکردید با واحد
          اداری آموزشگاه ریاضی مهندس عطار تماس حاصل فرمائید و یا از طریق فرم پیش
          ثبت نام اقدام فرمایید.
        </p>
      }
    >
      <form
        className="flex flex-col gap-lg"
        onSubmit={handleSubmit(submitForm)}
        noValidate
      >
        <div>
          <input
            placeholder="کدملی یا شماره همراه"
            aria-label="کدملی یا شماره همراه"
            type="number"
            name="username"
            className={AUTH_FIELD}
            {...register("username", {
              maxLength: {
                value: 10,
                message: "کدملی خود را به طور صحیح وارد کنید",
              },
              minLength: {
                value: 10,
                message: "کدملی خود را به طور صحیح وارد کنید",
              },
            })}
            onChange={(e) => setUsername(e.target.value)}
          />
          {errors?.username && (
            <p className="mt-xs text-base text-danger">
              {errors.username.message}
            </p>
          )}
        </div>

        <div>
          <div className="relative">
            <input
              placeholder="رمز عبور"
              aria-label="رمز عبور"
              type={showPassword ? "text" : "password"}
              name="password"
              className={`${AUTH_FIELD} pe-[4.8rem]`}
              {...register("password", {
                minLength: {
                  value: 4,
                  message: "رمز خود را به طور صحیح وارد کنید",
                },
              })}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label="نمایش رمز عبور"
              aria-pressed={showPassword}
              className="absolute end-sm top-1/2 flex h-[3.2rem] w-[3.2rem] -translate-y-1/2 items-center justify-center rounded-soft text-ink-subtle transition-colors duration-150 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none"
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </button>
          </div>
          {errors?.password && (
            <p className="mt-xs text-base text-danger">
              {errors.password.message}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={login.loading}
          className="mt-md inline-flex items-center justify-center gap-sm rounded-pill bg-primary px-2xl py-md text-md font-medium text-white transition-colors duration-150 hover:bg-primary-hover active:bg-primary-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-60 motion-reduce:transition-none"
        >
          {login.loading && <CircularProgress size="2rem" color="inherit" />}
          ورود به سامانه
        </button>
      </form>
    </AuthPanel>
  );
};

export default Login;
